import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  HeartIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  PhotoIcon,
  EllipsisHorizontalIcon,
  PaperAirplaneIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolid } from "@heroicons/react/24/solid";

import DropdownMenuPortal from "../../ui/DropdownMenuPortal";
import AutoHeight from "../../ui/AutoHeight";
import CommentItem from "../CommentItem";
import ExpandableText from "../../ui/ExpandableText";
import VisibilityPill from "../../ui/VisibilityPill";
import Spinner from "../../ui/Spinner";
import ConfirmModal from "../../ui/ConfirmModal";
import ReportModal from "../../ui/ReportModal";
import ShareModal from "../../ui/ShareModal";
import EditNoteModal from "./EditNoteModal";
import { useNoteLike } from "../../hooks/useNoteLike";
import { useComments } from "../../hooks/useComments";
import { useTimeAgo } from "../../hooks/useTimeAgo";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useToast } from "../../hooks/useToast";
import { useAuth } from "../../contexts/AuthContext";
import { getNoteMenuItems } from "../../utils/getMenuItems";
import { api } from "../../api";
import { logError } from "../../utils/logger";
import { formatAddressString } from "../../utils/geocoding";
import { Avatar } from "../avatars/Avatar";
import { formatApproximateAddress } from "../../utils/locationPrivacy";

function ImageWithPlaceholder({
  src,
  alt = "",
  /** Set the reserved height here; same across all cards */
  heightClass = "h-32 md:h-40",
  className = "", // extra classes for the outer wrapper (e.g., rounded/border)
  imgClassName = "", // extra classes for the img (e.g., group-hover:scale)
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <div className={`relative overflow-hidden ${heightClass} ${className}`}>
      {/* Skeleton placeholder reserves space & pulses */}
      {!loaded && !failed && (
        <div
          className="absolute inset-0 flex items-center justify-center animate-pulse bg-surface-sunken"
          aria-hidden="true"
        >
          <div className="relative z-10">
            <Spinner size={24} className="text-text-muted/60" />
          </div>
        </div>
      )}

      {/* Actual image (cross-fades in) */}
      {!failed ? (
        <img
          src={src}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
            loaded ? "opacity-100" : "opacity-0"
          } ${imgClassName}`}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          {...rest}
        />
      ) : (
        /* Simple error fallback */
        <div className="absolute inset-0 grid place-items-center text-text-muted bg-surface-sunken">
          <PhotoIcon className="h-6 w-6" />
        </div>
      )}
    </div>
  );
}

/**
 * Wrapping the NoteCard with forwardRef
 *  Let's the parent of this component have a reference to an internal element in NoteCard
 *    A ref to a component function returns null, so we must wrap the function in a forwardRef
 *    and Specify what element we want to expose.
 *      We are using useImperativeHandle, in this case, to expose commentsEl
 */
const NoteCard = forwardRef(function NoteCard(
  {
    note,
    showClose = false,
    onClose,
    onRequestPhotoView, // (index:number) => void
    menuBoundaryRef, // clamp boundary (e.g., modal card or list scroller)
    onAnyMenuOpenChange, // notify parent when any menu opens/closes
    isOwner = false, // controls visibility badge
    onNoteDeleted, // callback when note is deleted
    onNoteUpdated, // callback when note is updated
    initialCommentsOpen = false, // whether to open comments initially (from URL params)
    highlightCommentId = null, // comment ID to highlight (from URL params)
  },
  ref,
) {
  /* ------------- Auth & user state ---------------- */
  const { user } = useAuth();
  const { toast } = useToast();

  /* ------------- local UI state ---------------- */
  /* Controls wether the comments are visible */
  const [showComments, setShowComments] = useState(initialCommentsOpen);

  /* Comment input state */
  const [commentText, setCommentText] = useState("");

  /* Local comment count for optimistic updates */
  const [commentCount, setCommentCount] = useState(note?.stats?.comments ?? 0);

  /* Confirmation modal state */
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /* Report modal state */
  const [showReportModal, setShowReportModal] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);

  /* Edit modal state */
  const [showEditModal, setShowEditModal] = useState(false);

  /* Share modal state */
  const [showShareModal, setShowShareModal] = useState(false);

  // Use the like hook for backend-synced like functionality
  const {
    liked: likedNote,
    likeCount,
    toggleLike: toggleNoteLike,
  } = useNoteLike(
    note?.id,
    note?.isLikedByCurrentUser ?? false, // ← From backend data
    note?.stats?.likes ?? 0,
  );

  // Use the comments hook for backend-synced comments
  const {
    comments: fetchedComments,
    loading: loadingComments,
    loadingMore,
    posting,
    hasMore,
    error: commentsError,
    loadMore,
    addComment,
    deleteComment,
    refetch,
  } = useComments(note?.id, highlightCommentId);

  /**
   * Reference to the comments section (container)
   *  Helps with infinite scroll detection
   *  Helps stick the comments composer to the right place
   *  Used to expose the container reference to parent component
   */
  const commentsRef = useRef(null);

  // Set up infinite scroll for comments (passing commentsRef as the scroll container)
  const sentinelRef = useInfiniteScroll(
    loadMore, // Callback to load more
    hasMore, // Whether there are more items
    loadingMore, // Whether currently loading
    0.1, // Trigger when 10% visible
    commentsRef, // Nested scroll container
  );

  // Update comment count when note prop changes (e.g., when modal reopens with fresh data)
  useEffect(() => {
    setCommentCount(note?.stats?.comments ?? 0);
  }, [note?.stats?.comments]);

  // single menu state + which anchor (header or footer)
  /**
   * Tracks whether the menu on the note is open or not
   */
  const [noteMenuOpen, setNoteMenuOpen] = useState(false);
  /**
   * Tracks where the menu's anchor (ellipsis) is.
   *  Could be header or footer depending on where the notecard is rendered.
   */
  const [noteMenuAnchor, setNoteMenuAnchor] = useState(null); // 'header' | 'footer' | null

  /**
   * Each comment has a menu, this tracks which one is open.
   *  Only one menu can be opened at once
   */
  const [openCommentMenuId, setOpenCommentMenuId] = useState(null);

  /**
   * Reference to the Note's menu's anchor
   */
  const headerEllipsisRef = useRef(null);
  const footerEllipsisRef = useRef(null);

  const images = note?.images || [];
  const hasImages = images.length > 0;

  // Auto-updating time display
  const { display: noteTimeAgo, fullDateTime: noteFullDate } = useTimeAgo(note?.createdAt);

  /**
   * Handles the toggle to open/close comments
   */
  const toggleComments = useCallback(
    () => setShowComments((prev) => !prev),
    [],
  );

  /**
   * Used to notify the parent that a menu is opened
   *  Parents will use this notification to disable scrolling.
   * This runs when the dependencies (dependency array) change.
   */
  useEffect(() => {
    const open = noteMenuOpen || openCommentMenuId !== null;
    onAnyMenuOpenChange?.(open);
  }, [noteMenuOpen, openCommentMenuId, onAnyMenuOpenChange]);

  /**
   * Sorts the comments by id (will be by timestamp)
   * Uses useMemo so we do not re-sort the comments on every render
   *  Only re-sorts when fetchedComments changes
   */
  const sortedComments = useMemo(() => {
    const arr = fetchedComments || [];
    const copy = Array.isArray(arr) ? arr.slice() : [];
    copy.sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
    return copy;
  }, [fetchedComments]);

  /**
   * Handle posting a new comment
   */
  const handlePostComment = useCallback(async () => {
    if (!commentText.trim() || posting) return;

    try {
      // Optimistically update the count immediately
      setCommentCount((prev) => prev + 1);

      const result = await addComment(commentText);
      setCommentText(""); // Clear input after successful post

      // Update with actual count from backend if different (backup)
      if (result?.stats?.comments !== undefined) {
        setCommentCount(result.stats.comments);
      }
    } catch (err) {
      // Revert optimistic update on error
      setCommentCount((prev) => prev - 1);
      logError("Failed to post comment:", err);
      toast.error("Failed to post comment");
    }
  }, [commentText, posting, addComment, toast]);

  /**
   * Handle Enter key to post comment
   */
  const handleCommentKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handlePostComment();
      }
    },
    [handlePostComment],
  );

  // menu helpers
  const closeNoteMenu = useCallback(() => {
    setNoteMenuOpen(false);
    setNoteMenuAnchor(null);
  }, []);
  /**
   * Just in case, close the comment menu
   * Set Note menu anchor to nothing or header
   * Toggles the menu open/close
   */
  const toggleHeaderMenu = useCallback(() => {
    setOpenCommentMenuId(null);
    setNoteMenuAnchor((prev) => (prev === "header" ? null : "header"));
    setNoteMenuOpen((v) => (noteMenuAnchor === "header" ? !v : true));
  }, [noteMenuAnchor]);

  /**
   * Just in case, close the comment menu
   * Set Note menu anchor to nothing or footer
   * Toggles the menu open/close
   */
  const toggleFooterMenu = useCallback(() => {
    setOpenCommentMenuId(null);
    setNoteMenuAnchor((prev) => (prev === "footer" ? null : "footer"));
    setNoteMenuOpen((v) => (noteMenuAnchor === "footer" ? !v : true));
  }, [noteMenuAnchor]);

  /**
   * Handle note deletion
   */
  const handleDeleteNote = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const confirmDeleteNote = useCallback(async () => {
    try {
      await onNoteDeleted?.(note?.id);
      toast.success("Note deleted successfully", 3000);
    } catch (err) {
      logError("Failed to delete note:", err);
      toast.error("Failed to delete note");
    }
  }, [note?.id, onNoteDeleted, toast]);

  /**
   * Handle note reporting
   */
  const handleReportNote = useCallback(() => {
    setShowReportModal(true);
  }, []);

  const handleSubmitReport = useCallback(
    async (reason, details) => {
      try {
        setSubmittingReport(true);
        await api.reportNote(note?.id, reason, details);
        setShowReportModal(false);
        toast.success(
          "Report submitted. Thank you for helping keep Traces safe.",
          3000,
        );
      } catch (err) {
        logError("Failed to report note:", err);
        toast.error("Failed to submit report", 3000);
      } finally {
        setSubmittingReport(false);
      }
    },
    [note?.id, toast],
  );

  /**
   * Handle note editing
   */
  const handleEditNote = useCallback(() => {
    setShowEditModal(true);
  }, []);

  const handleUpdateNote = useCallback(
    async (updates) => {
      try {
        await api.updateNote(note?.id, {
          title: updates.title,
          body: updates.noteBody,
          visibility: updates.visibility,
          locationPrecision: updates.locationPrecision,
          showAddress: updates.showAddress,
          images: [...updates.existingImages, ...updates.newImages],
        });
        setShowEditModal(false);
        toast.success("Note updated successfully", 3000);

        // Notify parent to refetch/update data
        await onNoteUpdated?.(note?.id);
      } catch (err) {
        logError("Failed to update note:", err);
        toast.error("Failed to update note", 3000);
      }
    },
    [note?.id, onNoteUpdated, toast],
  );

  /**
   * Handle note sharing - opens share modal
   */
  const handleShareNote = useCallback(() => {
    setShowShareModal(true);
  }, []);

  /**
   * Generate context-aware menu items based on note ownership
   */
  const isNoteOwner = note?.owner?.id === user?.id;
  const noteMenuItems = useMemo(
    () =>
      getNoteMenuItems(isNoteOwner, note?.visibility, {
        onEdit: handleEditNote,
        onShare: handleShareNote,
        onDelete: handleDeleteNote,
        onReport: handleReportNote,
      }),
    [
      isNoteOwner,
      note?.visibility,
      handleEditNote,
      handleShareNote,
      handleDeleteNote,
      handleReportNote,
    ],
  );

  /**
   * useImperativeHandle to expose a specific element to the parent
   * If a parent passes in a ref, they can do ref.current.commentsEl for eg to access the comments sroller
   *  This ref can be used to programmatically scroll into view after routing
   */
  useImperativeHandle(ref, () => ({ commentsEl: commentsRef.current }), []);

  /**
   * Visibility badge (public/private)
   *  Only rendered when current note is by the current user
   */
  const vis = note?.visibility;
  const visibilityBadge =
    isOwner && vis ? <VisibilityPill visibility={vis} size="sm" /> : null;

  // pick the active anchor ref for the menu
  const activeAnchorRef =
    noteMenuAnchor === "header" ? headerEllipsisRef : footerEllipsisRef;

  return (
    /**
     * p-5: padding on x & y (all sides) by 5
     * relative: Used to provide positioning context (dimensions) to any child element
     *  An "absolute" child will position itself relative to its nearest "relative" parent
     */
    <div className="p-5 relative">
      {/* Close (optional) */}
      {showClose && (
        <button
          onClick={onClose}
          /**
           * absolute: An uneffectable positioning relative to parent's relative position (right-3 top-3 z-30).
           *    Element is taken out of normal layout flow.
           * inline-flex: Acts like "flex". Takes up as much space as needed (h-8 w-8)
           *    other inline elements can be on the same line as it, unlike "flex"
           */
          className="absolute right-3 top-3 z-30 inline-flex h-8 w-8 items-center justify-center rounded-full bg-surface-elevated/90 text-text-secondary shadow ring-1 ring-border transition-all duration-200 hover:bg-surface-elevated hover:shadow-lg hover:-translate-y-0.5 hover:scale-105"
          aria-label="Close modal"
        >
          ✕
        </button>
      )}

      {/* Header: owner + badge.
          When showClose is true, add a touch of right padding so the badge doesn't sit under ✕ */}
      <div
        /**
         * flex: Container becomes a flex layout. Arranged in rows (left to right)
         *    By default flex items take up the space that they need.
         *    By default flex items (flex-row) are aligned to the left
         * items-center: center the elements in this flex container vertically (around the y axis)
         * gap-3: put a gap of 3 between elements in this container
         * mb-4: put a margin of 4 at the botto,
         * pr-10: padding to the right of 10
         */
        className={`flex items-center gap-3 mb-4 ${showClose ? "pr-10" : ""}`}
      >
        <Avatar
          name={note?.owner?.name}
          avatarUrl={note?.owner?.avatar}
          size="md"
        />
        {/**
         * Element in the flex-row container aligned to the left
         * min-w-0: overriding the minimum shrinking width of the flex element
         *    (From shortest unbroken work to actual width of 0)
         *    Without min-w-0, flex items will only be able to shrink to the shortest unbroken word
         *      The word could be longer than the container, which would cause an overflow
         *    With a combination of min-w-0 and truncate,
         *    the content of a flex element is able to be truncated at any place within a word to fit the element's container
         */}
        <div className="min-w-0">
          {/**
           * truncate: works with min-w-0 to shorten elements to fit within the container
           */}
          <div className="text-sm font-semibold text-text-primary truncate">
            {note?.owner?.name}
          </div>
          <div className="text-xs text-text-muted truncate">
            <span title={noteFullDate}>{noteTimeAgo}</span>
            {note?.editedAt && (
              <span className="text-text-muted ml-1">(edited)</span>
            )}
            {!note?.isViewedByCurrentUser && !note?.isOwner && (
              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary">
                New
              </span>
            )}
          </div>
        </div>
        {/**
         * ml-auto: This element will use all the available space on the its left
         *    And push its content as far right as it can go
         */}
        <div className="ml-auto flex items-center gap-3">
          {visibilityBadge}

          {/* Header ellipsis only when NO close button */}
          {!showClose && (
            <div className="relative">
              <button
                ref={headerEllipsisRef}
                className="p-1.5 rounded-full hover:bg-surface-sunken"
                aria-label="More note options"
                title="More options"
                onClick={toggleHeaderMenu}
              >
                <EllipsisHorizontalIcon className="h-5 w-5 text-primary" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Title & Body */}
      <h3 className="text-lg font-semibold text-text-primary">{note?.title}</h3>
      {note?.address && note?.showAddress !== false && (
        /**
         * inline-flex:
         *    1. Makes the div (usually a block element) act like an inline element,
         *      it only takes up as much space as needed instead of spanning the entire parent's width
         *    2. Makes the container a flex-row element
         * items-center: vertical (top to bottom) alignment of the children elements
         */
        <div className="text-xs text-text-muted mb-1 inline-flex items-center gap-1 rounded-full">
          <MapPinIcon className="h-3.5 w-3.5" />
          <span>
            {note.locationPrecision === "approximate"
              ? formatApproximateAddress(note.address)
              : formatAddressString(note.address)}
          </span>
        </div>
      )}
      <AutoHeight className="mb-4">
        <ExpandableText text={note?.body} limit={260} />
      </AutoHeight>

      {/* Inline photo preview */}
      {hasImages && (
        <div
          /**
           * overflow-hidden: any overflow out of the container is hidden
           *    The scale of the photo by 1.02 will overflow, so its hidden.
           */
          className="relative overflow-hidden rounded-lg border border-border group cursor-pointer"
          onClick={() => onRequestPhotoView?.(0)}
          aria-label="Open photos"
        >
          {/**
           * w-full: fill the full width of the container (minus container's padding)
           * max-h-32 & md:max-h-40: max height (32 or 40, depending on screen)
           * group-hover:scale-[1.02]: when hovered over, scale the image by 1.02
           */}
          {/* <img
            src={images[0].src}
            alt={note?.title}
            className="w-full object-cover transition-transform duration-300 group-hover:scale-[1.02] max-h-32 md:max-h-40"
            loading="lazy"
            decoding="async"
          /> */}
          <ImageWithPlaceholder
            src={images[0].src}
            alt={note?.title}
            heightClass="h-32 md:h-40"
            className=""
            imgClassName="transition-transform duration-300 group-hover:scale-[1.02]"
          />
          {/**
           * inline-flex:
           *    1. Makes div an inline element, only takes up as much space as needed
           *    2. Container becomes a flex-row element
           * gap-1: elements of the flex container are spaced by a gap-1 (0.25 rem or 4px)
           */}
          <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-surface-elevated/90 px-2.5 py-1 text-xs font-medium text-white/60 backdrop-blur border border-white/30">
            <PhotoIcon className="h-3.5 w-3.5" />
            <span>{images.length}</span>
          </div>
        </div>
      )}

      {/* Footer actions: Like, Comment, Ellipsis (shown here only when showClose is true) */}
      {/**
       * Flex: container becomes a flex-row container.
       * items-center: Container's children get aligned (top to bottom)
       * gap-2: gap between the children in the flex container
       * mt-4: Margin on top of the container (div element)
       */}
      <div className="flex items-center gap-2 mt-4">
        <div className="inline-flex items-center gap-1">
          <button
            onClick={toggleNoteLike}
            className="text-text-secondary hover:text-black transition-colors transition-transform duration-150 active:scale-95"
            aria-pressed={likedNote}
          >
            {likedNote ? (
              <HeartSolid className="h-5 w-5 text-destructive" />
            ) : (
              <HeartIcon className="h-5 w-5" />
            )}
          </button>
          <span className="text-text-secondary">{likeCount}</span>
        </div>

        <div className="inline-flex items-center gap-1">
          <button
            onClick={toggleComments}
            className="transition-colors"
            aria-expanded={showComments}
          >
            <ChatBubbleOvalLeftEllipsisIcon
              className={`h-5 w-5 ${
                showComments
                  ? "text-primary"
                  : "text-text-secondary hover:text-black"
              }`}
            />
          </button>
          <span className="text-text-secondary">{commentCount}</span>
        </div>

        {showClose && (
          /**
           * ml-auto:
           *    Add margin to the left of the container to fill the available space
           *    Pushes the element to the right side of the container
           */
          <div className="ml-auto relative">
            <button
              ref={footerEllipsisRef}
              className="p-1.5 rounded-full hover:bg-surface-sunken"
              aria-label="More note options"
              title="More options"
              onClick={toggleFooterMenu}
            >
              <EllipsisHorizontalIcon className="h-5 w-5 text-primary" />
            </button>
          </div>
        )}
      </div>

      {/* Shared dropdown menu (portal) — anchored to whichever ellipsis is active */}
      {noteMenuOpen && activeAnchorRef?.current && (
        <DropdownMenuPortal
          anchorRef={activeAnchorRef}
          boundaryRef={menuBoundaryRef}
          scrollRefs={[menuBoundaryRef]}
          onClose={closeNoteMenu}
          items={noteMenuItems}
          side="top"
          align={noteMenuAnchor === "header" ? "left" : "right"}
        />
      )}

      {/* Comments */}
      <div
        className={`transition-all duration-150 ease-out ${
          showComments ? "opacity-100 mt-4" : "max-h-0 opacity-0"
        }`}
        aria-hidden={!showComments}
      >
        {showComments && (
          <section
            id="comments"
            aria-label="Comments"
            /**
             * flex: Makes container a flex container
             * flex-col: Children (comment items, composer, etc...) of this container will be stack in a column (on top of each other)
             * overflow-y-auto: Automatically adds a scroll bar if the contents length exceeds the container's height
             * pretty-scroll: defined in index.css, makes the width and height of the scrollbar 0
             */
            className="flex flex-col overflow-y-auto pretty-scroll max-h-[30vh]"
            /**
             * clamp: clamp(minimum %, preferred %, max %) of the viewport
             *    Tells the browser the preferred height of the comment section in relation to the viewport
             *    Because of overflow-y-auto, if the height preferrence is exceeded, it adds a scrollbar
             */
            // style={{ height: "clamp(36vh, 50vh, 56vh)" }}
            ref={commentsRef}
          >
            {/**
             * sticky: stays at the position it is defined at
             */}
            <div className="sticky top-0 z-10 bg-surface-elevated">
              {/**
               * flex: container is a flex container
               * items-center: center the children vertically (top to bottom)
               * gap-2: gap between each child
               * px-3: padding on the x-axis
               * pt-1: paddding on top
               * pd-1.5: padding on the bottom
               */}
              <div className="flex items-center gap-2 px-3 pt-1 pb-1.5">
                <input
                  placeholder="Write a comment…"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={handleCommentKeyDown}
                  disabled={posting}
                  /**
                   * flex-1: Grow to fill the available space
                   */
                  className="flex-1 bg-surface border border-border rounded-full px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:bg-surface-elevated disabled:opacity-50"
                />
                <button
                  onClick={handlePostComment}
                  disabled={!commentText.trim() || posting}
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-text-inverse shadow transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow disabled:hover:translate-y-0 disabled:hover:scale-100"
                  aria-label="Post comment"
                >
                  {posting ? (
                    <Spinner size={16} className="text-text-inverse" />
                  ) : (
                    <PaperAirplaneIcon className="h-4 w-4 rotate-0" />
                  )}
                </button>
              </div>
              <div className="h-px bg-surface-sunken" />
            </div>

            {/* Loading State - Initial load */}
            {loadingComments && sortedComments.length === 0 && (
              <div className="flex items-center justify-center py-8 text-text-muted">
                <div className="flex items-center gap-2">
                  <Spinner size={20} className="text-text-muted" />
                  <span className="text-sm">Loading comments...</span>
                </div>
              </div>
            )}

            {/* Error State */}
            {commentsError && !loadingComments && (
              <div className="px-3 py-6 text-center">
                <p className="text-sm text-destructive mb-2">
                  Failed to load comments
                </p>
                <button
                  onClick={refetch}
                  className="text-xs text-primary hover:text-primary font-medium"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Empty State - No comments yet */}
            {!loadingComments &&
              !commentsError &&
              sortedComments.length === 0 && (
                <div className="px-3 py-8 text-center">
                  <ChatBubbleOvalLeftEllipsisIcon className="h-12 w-12 mx-auto text-text-disabled mb-2" />
                  <p className="text-sm text-text-muted mb-1">
                    No comments yet
                  </p>
                  <p className="text-xs text-text-muted">
                    Be the first to comment!
                  </p>
                </div>
              )}

            {/**
             * space-y-2: space between elements (on the y-axis)
             */}
            <ul className="space-y-2 p-3 pt-2">
              {sortedComments.map((c) => (
                <CommentItem
                  key={c.id}
                  c={c}
                  noteId={note?.id}
                  noteOwnerId={note?.owner?.id}
                  onOpenMenu={setOpenCommentMenuId}
                  onCloseMenu={() => setOpenCommentMenuId(null)}
                  openCommentMenuId={openCommentMenuId}
                  boundaryRef={menuBoundaryRef}
                  scrollRef={commentsRef}
                  onCommentDelete={deleteComment}
                  isHighlighted={highlightCommentId === c.id}
                />
              ))}
            </ul>

            {/* Infinite scroll sentinel - triggers loading when visible */}
            <div
              ref={sentinelRef}
              className="h-20 flex items-center justify-center"
            >
              {loadingMore && (
                <div className="flex items-center gap-2 text-text-muted">
                  <Spinner size={16} className="text-text-muted" />
                  <span className="text-xs">Loading more comments...</span>
                </div>
              )}
              {!hasMore && sortedComments.length > 3 && (
                <p className="text-xs text-text-muted">
                  You've reached the end
                </p>
              )}
            </div>
          </section>
        )}
      </div>

      {/* Delete Note Confirmation Modal */}
      <ConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteNote}
        title="Delete note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
        confirmVariant="danger"
      />

      {/* Report Note Modal */}
      <ReportModal
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        onSubmit={handleSubmitReport}
        type="note"
        submitting={submittingReport}
      />

      {/* Edit Note Modal */}
      {showEditModal && (
        <EditNoteModal
          note={note}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateNote}
        />
      )}

      {/* Share Modal */}
      <ShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        note={note}
      />
    </div>
  );
});

export default NoteCard;
